import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/core/firebase';

interface UseFirestoreCollectionResult<T> {
  data: T[];
  error: string | null;
  loading: boolean;
}

/**
 * Subscribe to a Firestore collection query in real time.
 *
 * Returns `{ data, error, loading }`. The subscription is torn down
 * automatically when the component unmounts or inputs change.
 *
 * @param collectionPath - Collection name, e.g. `"workoutSessions"`.
 *                         Pass `null` or `undefined` to skip the subscription.
 * @param constraints    - Firestore query constraints (`where`, `orderBy`, `limit`, etc.).
 *                         **Must be a stable reference** (e.g. wrapped in `useMemo`).
 *
 * @example
 * ```ts
 * const constraints = useMemo(
 *   () => [where('trainerId', '==', trainerId)],
 *   [trainerId],
 * );
 * const { data: sessions, error } = useFirestoreCollection<WorkoutSession>(
 *   trainerId ? 'workoutSessions' : null,
 *   constraints,
 * );
 * ```
 */
export function useFirestoreCollection<T extends { id: string }>(
  collectionPath: string | null | undefined,
  constraints: QueryConstraint[] = [],
): UseFirestoreCollectionResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!collectionPath);

  useEffect(() => {
    if (!collectionPath) {
      setData([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(collection(db, collectionPath), ...constraints);

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as T),
        );
        setData(docs);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error(`[useFirestoreCollection] Error on "${collectionPath}":`, err);
        setError(err.message || 'Failed to load collection');
        setLoading(false);
      },
    );

    return () => unsub();
    // constraints is caller-provided — caller must memoize for stability
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionPath, ...constraints]);

  return { data, error, loading };
}
